import { Injectable, signal } from '@angular/core';

/**
 * LobNameContext
 *
 * Provides the current assessment's Line of Business (LOB) name to child components. Provided at the AssessmentRenderer
 * component level so each assessment instance has its own context. Set during ngOnInit from the lobName input of
 * AssessmentRenderer. CEDIS uses it to filter active-today ICD lookups by LOB.
 *
 * @example
 *   AssessmentRenderer provides and sets it:
 *   ```
 *   providers: [LobNameContext]
 *   // ngOnInit: inject(LobNameContext).setLobName(this.lobName())
 *   ```
 *
 *   BaseControlWrapper reads it:
 *   ```
 *   private readonly lobNameCtx = inject(LobNameContext, { optional: true });
 *   // this.lobNameCtx?.lobName() → 'Medicare Advantage'
 *   ```
 */
@Injectable()
export class LobNameContext {
  private readonly _lobName = signal<string | undefined>(undefined);

  /** The current LOB name (e.g., 'Medicare Advantage') — undefined if not set. */
  readonly lobName = this._lobName.asReadonly();

  /** Called by AssessmentRenderer after inputs are resolved. */
  setLobName(name: string | undefined): void {
    this._lobName.set(name || undefined);
  }
}
